import React, { ReactElement, Suspense, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import type { IDetectedBarcode, IScannerError } from '@yudiel/react-qr-scanner'
import { Box, Button, styled } from '@mui/material'
import { useAppId } from 'src/app/AppProvider'
import { errorMapper } from 'src/i18n/ErrorMapper'
import { useTranslate } from 'src/i18n/useMessageSource'
import { Configuration, MachineTag, MachinesApi } from 'src/service/backend/api'
import { HttpContext } from 'src/service/backend/http/HttpContext'
import { ErrorMessage } from 'src/ui-shared/base/error-message/ErrorMessage'
import { useRequiredParams } from 'src/ui-shared/base/hooks/useRequiredParams'
import { LoadingIndicator } from 'src/ui-shared/base/loading-indicator/LoadingIndicator'
import { useShowSnackbar } from 'src/ui-shared/base/snackbar/SnackbarProvider'
import { EMPTY_UUID } from 'src/ui-shared/constants/Constants'
import { useSharedStyles } from 'src/ui-shared/constants/Shared.style'
import { MachineTab } from 'src/ui/page/common/machine/details/MachineTab'

type FacingMode = 'user' | 'environment'

const BoxContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(9),
  },
}))

const QrReaderParentBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    width: '100%',
  },
}))

const onResize = (qrReaderParent: HTMLElement, qrReader: HTMLElement) => {
  const parentWidth = qrReaderParent.clientWidth
  const parentHeight = qrReaderParent.clientHeight

  let qrReaderWidth = 100
  if (parentWidth > parentHeight) {
    const calculatedWidthPercentage = (parentHeight / parentWidth) * 100
    qrReaderWidth = Math.ceil(calculatedWidthPercentage)
  }

  const qrReaderWidthString = `${qrReaderWidth}%`
  qrReader.style.width = qrReaderWidthString
}

const AsyncQrReader = React.lazy(() => import('@yudiel/react-qr-scanner').then(({ Scanner }) => ({ default: Scanner })))

export const MachineQrCodeScanner = (): ReactElement => {
  const navigate = useNavigate()
  const translate = useTranslate()
  const { machineId } = useRequiredParams(['machineId'])
  const { classes: sharedClasses } = useSharedStyles()
  const appId = useAppId()
  const showSnackbar = useShowSnackbar()

  const httpConfiguration: Configuration = useContext(HttpContext)
  const machinesApi = new MachinesApi(httpConfiguration)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [facingMode, setFacingMode] = useState<FacingMode>('environment')
  const [updatingMachineQrCode, setUpdatingMachineQrCode] = useState<boolean>(false)

  useEffect(() => {
    const qrReaderParent = document.getElementById('qrReaderParent')
    const qrReader = document.getElementById('qrReader')
    if (qrReaderParent && qrReader) {
      window.addEventListener('resize', onResize.bind(this, qrReaderParent, qrReader))
    }
  }, [])

  const createMachineTag = (qrCode: string) => {
    const tag: MachineTag = {
      id: EMPTY_UUID,
      contentUrl: qrCode,
    }
    return tag
  }

  const updateMachineQrCode = async (qrCode: string) => {
    setUpdatingMachineQrCode(true)
    try {
      const machine = await machinesApi.machinesMachineIdGet(machineId, []).then((machine) => machine)
      if (machine.washmaster !== null && machine.washmaster !== undefined) {
        machine.washmaster.tag = createMachineTag(qrCode)
      }
      machinesApi
        .machinesMachineIdPut(machineId, [], machine)
        .then((_data) => {
          navigate(`/${appId}/machines/${machineId}/view/${MachineTab.OVERVIEW}`)
        })
        .catch((err) => {
          const errorMessage = errorMapper(err, translate)
          console.error(errorMessage, err)
          showSnackbar(errorMessage, 'error')
          setUpdatingMachineQrCode(false)
        })
      setUpdatingMachineQrCode(false)
    } catch (err) {
      const errorMessage = errorMapper(err, translate)
      console.error(errorMessage, err)
      showSnackbar(errorMessage, 'error')
      setUpdatingMachineQrCode(false)
    }
  }

  const onScanHandle = (result: IDetectedBarcode[]) => {
    if (result.length > 0 && result[0].rawValue && !updatingMachineQrCode) {
      updateMachineQrCode(result[0].rawValue)
    }
  }

  const scannerErrorHandle = (err: IScannerError) => {
    console.error(err.message, err)
    setErrorMessage(err.message)
  }

  const handleFacingModeChange = () => {
    setFacingMode(facingMode === 'user' ? 'environment' : 'user')
  }

  return (
    <BoxContainer sx={{ mt: 2 }}>
      <Box
        sx={{
          px: 3,
          mb: 2,
        }}
      >
        {errorMessage ? <ErrorMessage message={errorMessage} /> : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Button
          className={sharedClasses.ButtonMargin}
          variant="contained"
          color="primary"
          onClick={handleFacingModeChange}
        >
          {facingMode === 'user' ? translate('frontCamera') : translate('backCamera')}
        </Button>
      </Box>
      <Box
        id="qrReaderParent"
        sx={{
          height: '70vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <LoadingIndicator loading={updatingMachineQrCode} />
          <QrReaderParentBox id="qrReader" sx={{ width: '45%' }}>
            <Suspense fallback={<div style={{ textAlign: 'center' }}>{translate('autocompleteLoading')}</div>}>
              <AsyncQrReader
                scanDelay={1500}
                constraints={{
                  facingMode,
                }}
                allowMultiple
                formats={['qr_code']}
                onScan={onScanHandle}
                onError={scannerErrorHandle}
              />
            </Suspense>
          </QrReaderParentBox>
        </Box>
      </Box>
    </BoxContainer>
  )
}
